import React, {useEffect} from 'react'
import {useAuth0} from '@auth0/auth0-react'
import {useDispatch} from 'react-redux'
import {setEnvConfig, setUserData} from '../app-store/userSlice'
import {getUserRoles} from '../utils/get-user-roles'
import {setActualRole, setIsSwitchRoleUser} from '../app-store/userRoleSlice'
import {client} from '../utils/api-client'

/**
 * @typedef {import('@auth0/auth0-react').User & { network_id: string }} User
 */

/**
 * @typedef {{
 *  demoPresenter: string,
 *  demoPresenterImg: string,
 *  networkEmail: string,
 *  sfUserId: string,
 * }} ThryvUser
 */

/**
 * @type {React.Context<{user?: User}>}
 */
const UserContext = React.createContext(null)

/**
 *
 * @param {string | undefined} email
 * @returns {boolean | undefined}
 */
function isThryvUser(email) {
  const domain = email?.split('@')[1]
  return domain?.toLowerCase().includes('thryv')
}

export function UserProvider({children}) {
  const {isLoading, isAuthenticated, error, user} = useAuth0()
  const [thryvUser, setThryvUser] = React.useState(null)

  const dispatch = useDispatch()

  const getEnvironment = (hostname = '') => {
    // Handle non-string values safely
    const host =
      typeof hostname === 'string' ? hostname.toLowerCase().trim() : ''

    if (!host) {
      return 'local'
    }

    if (host.includes('localhost')) {
      return 'local'
    }

    // Order matters
    if (host.includes('dev-testing')) {
      return 'dev-testing'
    }

    if (host.includes('dev')) {
      return 'dev'
    }

    if (host.includes('staging')) {
      return 'staging'
    }

    if (host.includes('blue')) {
      return 'blue'
    }

    if (host.includes('green')) {
      return 'green'
    }

    return 'production'
  }

  useEffect(() => {
    dispatch(setUserData({isLoading, isAuthenticated, error, user}))
    const userRole = getUserRoles(user, 'isSwitchRoleUser')
    dispatch(setActualRole(userRole))
    const hostname = window.location.hostname

    const envConfig = {
      hostname,
      env: getEnvironment(hostname),
    }

    dispatch(setEnvConfig(envConfig))
  }, [isLoading, isAuthenticated, error, user])

  const patchedUser = React.useMemo(() => {
    return {
      ...user,
      email: thryvUser?.networkEmail || user?.email,
    }
  }, [user, thryvUser])

  const loadThryvUserData = React.useCallback(async id => {
    try {
      const data = await client(
        `azuredirectory/getUserDetailsFromAD/?userId=${id}`,
      )
      setThryvUser(data)
    } catch (err) {
      console.error('Failed to load Thryv user data from Azure AD', err)
    }
  }, [])

  const fetchSwitchRoleUser = React.useCallback(async () => {
    return await client('keappartner/fetchConfigLookupValue/switchRoleUsers')
  }, [])

  React.useEffect(() => {
    if (!isAuthenticated || !user) return
    if (isThryvUser(user?.email)) {
      loadThryvUserData(user.network_id)
    }
    fetchSwitchRoleUser()
      .then(res => {
        const switchRoleUsers = res?.data?.[0]?.value
          ?.split(',')
          .map(r => r.trim())
        if (switchRoleUsers?.includes(user?.email)) {
          dispatch(setIsSwitchRoleUser(true))
        }
      })
      .catch(err => {
        console.error('Failed to fetch switch role users', err)
      })
      .finally(() => {
        // Re-evaluate after first ooe-service response so X-Stack-Color is in sessionStorage
        const hostname = window.location.hostname
        dispatch(setEnvConfig({hostname, env: getEnvironment(hostname)}))
      })
  }, [isAuthenticated, user])

  return (
    <UserContext.Provider
      value={{
        user: {
          ...patchedUser,
        },
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = React.useContext(UserContext)
  if (!context) {
    throw new Error("'useUser' can't be called outside 'UserProvider'")
  }
  return context
}
